import { DayEventHandler, MonthDayProps } from '@oberoninternal/travelbase-ds/components/calendar/PlannerMonth';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { gql } from '@apollo/client';
import addHours from 'date-fns/addHours';
import addMonths from 'date-fns/addMonths';
import endOfMonth from 'date-fns/endOfMonth';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfMonth from 'date-fns/startOfMonth';
import startOfToday from 'date-fns/startOfToday';
import isEqual from 'lodash.isequal';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import parseToDateString from '../constants/parseToDateString';
import {
    ActivityBookingTypesEnum,
    ActivityCheckoutModalTimeslotFragment,
    ActivityPlannerActivityFragment,
    ActivityPlannerDocument,
    ActivityPlannerQuery,
    ActivityPlannerQueryVariables,
    ActivityPlannerTimeslotFragment,
    TicketCartItemInput,
} from '../generated/graphql';
import usePlannerFetcher from '../hooks/planner/usePlannerFetcher';
import usePlannerQuery from '../hooks/planner/usePlannerQuery';
import usePrevious from '../hooks/usePrevious';
import parseDateTime from '../utils/parseDateTime';
import { getWeekday } from '../utils/weekday';
import ActivityPlannerTimeslots from './ActivityPlannerTimeslots';
import Planner, { PlannerContainer, PlannerMonthInstance } from './Planner';
import StickyBar from './StickyBar';
import ActivityPlannerMonth from './ActivityPlannerMonth';
import ActivityPlannerController from './ActivityPlannerController';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import ActivityTickets from './ActivityTickets';
import { Box } from '@rebass/grid';

export const pageQuery = gql`
    query ActivityPlanner($slug: String!, $startDate: String!, $endDate: String!) {
        activity(slug: $slug) {
            id
            externalBookingUrl
            bookingType
            timeslots(startDate: $startDate, endDate: $endDate) {
                ...ActivityPlannerTimeslot
            }
        }
    }

    fragment ActivityPlannerTimeslot on Timeslot {
        id
        allotment
        startDateTime
        endDateTime
        label
        rateGroup {
            releaseHours
            rates {
                id
                name
                price
            }
        }
    }

    # unused in gql, but its purpose is to generate prop types from
    fragment ActivityPlannerActivity on Activity {
        name
        bookingType
        externalBookingUrl
        company {
            ...CompanyOperatingHours
        }
    }
`;

const filterWithInterval = (timeslots: ActivityPlannerTimeslotFragment[], interval: Interval, exclusive = false) =>
    timeslots.filter(timeslot => {
        const within = isWithinInterval(parseDateTime(timeslot.startDateTime), interval);
        return exclusive ? !within : within;
    });

export const isUpcoming = (slot: ActivityPlannerTimeslotFragment, considerReleaseHours = false) =>
    // consider release hours by subtracting these from the startDateTime
    isAfter(
        addHours(
            parseDateTime(slot.startDateTime),
            (considerReleaseHours ? slot.rateGroup?.releaseHours ?? 0 : 0) * -1
        ),
        new Date()
    );

const getSelectedDayData = (
    selectedDay: Date | null,
    data: ActivityPlannerQuery | undefined,
    activity?: ActivityPlannerActivityFragment
) => {
    const timeslotsForSelectedDay =
        selectedDay &&
        data?.activity?.timeslots.filter(
            slot => isSameDay(parseDateTime(slot.startDateTime), selectedDay) && isUpcoming(slot)
        );
    const operatingHoursForSelectedDay =
        (selectedDay && activity?.company?.normalOperatingHours?.[getWeekday(selectedDay)]) ?? [];
    return { timeslotsForSelectedDay, operatingHoursForSelectedDay };
};

export type OnSelectScrollOptions = {
    scrollRef?: RefObject<HTMLDivElement | null>;
};

const useSelectedDayJumper = (
    selectedDay: Date | null,
    viewportRef: React.MutableRefObject<PlannerMonthInstance | null>,
    onSelectScrollOptions?: OnSelectScrollOptions
) => {
    const prev = usePrevious(selectedDay);

    useEffect(() => {
        if (selectedDay && viewportRef.current) {
            const { scrollRef } = onSelectScrollOptions ?? {};
            const { height, top } = viewportRef.current.getBoundingClientRect();
            const center = window.pageYOffset + top + height / 2;
            // scroll to the selected month
            viewportRef.current.plannerViewActions.jumpToMonth(selectedDay, true);

            // also scroll to the prices
            if (!isEqual(prev, selectedDay)) {
                const el = scrollRef?.current;
                el?.scrollTo({ top: center, behavior: 'smooth' });
            }
        }
        // only run when the selected day changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay]);
};

export const ticketsAvailable = (
    slot: ActivityPlannerTimeslotFragment | null,
    bookingType: ActivityBookingTypesEnum,
    strict = false
) => {
    if (bookingType === ActivityBookingTypesEnum.External) {
        // TODO strict?
        return true;
    }

    if (bookingType === ActivityBookingTypesEnum.Direct) {
        if (slot?.allotment !== null) {
            return (slot?.allotment ?? 0) > 0;
        }
    }

    return !strict;
};

export const slotIsBookable = (
    slot: ActivityPlannerTimeslotFragment,
    bookingType: ActivityBookingTypesEnum,
    selectedDay: Date,
    strict = false
) => {
    const hasTicketsAvailable = ticketsAvailable(slot, bookingType, strict);
    const hasExpired = !isUpcoming(slot, true);

    return isSameDay(selectedDay, parseDateTime(slot.startDateTime)) && hasTicketsAvailable && !hasExpired;
};

export const slotIsFull = (
    slot: ActivityPlannerTimeslotFragment,
    bookingType: ActivityBookingTypesEnum,
    strict = false
) => {
    const hasExpired = !isUpcoming(slot, true);
    return !ticketsAvailable(slot, bookingType, strict) && !hasExpired;
};

const useDayModifier = (selectedDay: Date | null, data: ActivityPlannerQuery | undefined) =>
    useCallback(
        (day: Date): MonthDayProps => {
            if (isBefore(day, startOfToday())) {
                return { variant: 'disabled' };
            }

            if (selectedDay && isSameDay(selectedDay, day)) {
                return { variant: 'selected' };
            }
            for (const slot of data?.activity?.timeslots ?? []) {
                if (isSameDay(parseDateTime(slot.startDateTime), day)) {
                    if (
                        slotIsBookable(slot, data?.activity?.bookingType ?? ActivityBookingTypesEnum.NotBookable, day)
                    ) {
                        return { variant: 'bookable' };
                    }
                    if (slotIsFull(slot, data?.activity?.bookingType ?? ActivityBookingTypesEnum.NotBookable)) {
                        return { variant: 'bookable-soldout' };
                    }
                    continue;
                }
            }

            return { variant: 'blocked' };
        },
        [data, selectedDay]
    );

export interface ActivityPlannerProps {
    slug: string;
    selectedDay: Date | null;
    setSelectedDay: (date: Date | null) => void;
    setSelectedDayIsBookable?: (isBookable: boolean) => void;
    onSelectScrollOpts?: OnSelectScrollOptions;
    activity?: ActivityPlannerActivityFragment;
    setTimeslot: (slot: ActivityPlannerTimeslotFragment | null) => void;
    isOpenModal?: boolean;
    setTicket?: (ticket: TicketCartItemInput | null) => Promise<void> | void;
    activityTimeslot?: ActivityCheckoutModalTimeslotFragment | null;
    ticket?: TicketCartItemInput | null;
    openActivityTickets?: boolean;
    openActivityPlanner?: boolean;
    onBack?: () => void;
    onActivityPlanner?: () => void;
    onModalClose?: () => void | undefined;
}

const getStartOpts = () => ({
    startDate: parseToDateString(startOfMonth(new Date())),
    endDate: parseToDateString(addMonths(startOfMonth(new Date()), 1)),
});

const ActivityPlanner = ({
    slug,
    selectedDay,
    setSelectedDay,
    setSelectedDayIsBookable,
    onSelectScrollOpts,
    activity,
    setTimeslot,
    isOpenModal,
    setTicket,
    activityTimeslot,
    ticket,
    openActivityTickets,
    openActivityPlanner,
    onBack,
    onActivityPlanner,
    onModalClose,
}: ActivityPlannerProps) => {
    const viewportRef = useRef<PlannerMonthInstance | null>(null);
    const [{ data, variables }, { query, readQuery, writeQuery }] = usePlannerQuery<
        ActivityPlannerQuery,
        ActivityPlannerQueryVariables
    >(
        {
            ...getStartOpts(),
            slug,
        },
        ActivityPlannerDocument
    );
    const { timeslotsForSelectedDay } = getSelectedDayData(selectedDay, data, activity);

    const fetcher = useCallback(
        async (months: Date[]) => {
            // normally we'd use fetchMore for this but somehow it doesn't look in the cache first.
            // also because we can only fetch 1 month per request we'll batch them and execute them in parallel.
            const promises: Promise<ActivityPlannerTimeslotFragment[]>[] = [];
            for (const month of months) {
                const vars = {
                    startDate: parseToDateString(month),
                    endDate: parseToDateString(endOfMonth(month)),
                };

                const execute = async () => {
                    const mergedVars = { ...variables, ...vars };
                    const result = await query(mergedVars);
                    const isMain = isEqual(mergedVars, variables);
                    if (isMain) {
                        const monthInterval = {
                            start: new Date(variables.startDate),
                            end: new Date(variables.endDate),
                        };
                        return filterWithInterval(result.data.activity?.timeslots ?? [], monthInterval);
                    }

                    return result.data.activity?.timeslots ?? [];
                };

                const promise = execute();
                promises.push(promise);
            }

            const results = await Promise.all(promises);

            const prev = readQuery();

            if (!prev?.activity) {
                return;
            }

            const monthsInterval = {
                start: startOfMonth(months[0]),
                end: startOfMonth(addMonths(months[months.length - 1], 1)),
            };

            writeQuery({
                ...prev,
                activity: {
                    ...prev.activity,
                    timeslots: [
                        ...filterWithInterval(prev.activity?.timeslots ?? [], monthsInterval, true),
                        ...results.flat(),
                    ],
                },
            });
        },
        [query, readQuery, variables, writeQuery]
    );

    const [, { isLoaded, loadMore }] = usePlannerFetcher(fetcher);
    const dayModifier = useDayModifier(selectedDay, data);

    useEffect(() => {
        if (!selectedDay && data) {
            const firstAvailableTimeSlot = data?.activity?.timeslots
                .filter(timeslot => isUpcoming(timeslot, true))
                ?.sort(
                    (timeslotA, timeslotB) =>
                        parseDateTime(timeslotA.startDateTime).valueOf() -
                        parseDateTime(timeslotB.startDateTime).valueOf()
                )[0];
            if (firstAvailableTimeSlot) {
                setSelectedDay(parseDateTime(firstAvailableTimeSlot.startDateTime));
            }
        }
    }, [data, selectedDay, setSelectedDay]);
    const dayIsBookable = useMemo(() => {
        if (selectedDay) {
            return !!data?.activity?.timeslots.find(slot =>
                slotIsBookable(slot, activity?.bookingType ?? ActivityBookingTypesEnum.NotBookable, selectedDay)
            );
        }

        return false;
    }, [selectedDay, data?.activity?.timeslots, activity?.bookingType]);

    useEffect(() => setSelectedDayIsBookable?.(dayIsBookable), [dayIsBookable, setSelectedDayIsBookable]);

    const { open, onClose, onOpen } = useSesame();

    const onDayClick: DayEventHandler = useCallback(
        ({ day }) => {
            if (selectedDay && isSameDay(day, selectedDay)) {
                setSelectedDay(null);
            } else {
                setSelectedDay(day);
                onOpen();
            }
        },
        [onOpen, selectedDay, setSelectedDay]
    );

    const intl = useIntl();
    const validatedSetTimeslot = useCallback(
        (slot: ActivityPlannerTimeslotFragment | null) => {
            if (slot && !isUpcoming(slot, true)) {
                window.alert(
                    intl.formatMessage({ defaultMessage: 'Dit tijdslot is verlopen, kies een ander tijdslot' })
                );
                return;
            }
            setTimeslot(slot);
        },
        [intl, setTimeslot]
    );
    const deviceSize = useDeviceSize();

    useSelectedDayJumper(selectedDay, viewportRef, onSelectScrollOpts);

    const onSetToday = () => setSelectedDay(new Date());

    const [openPlannerModal, setOpenPlannerModal] = useState<boolean | undefined>(isOpenModal);

    const openModal = () => {
        setOpenPlannerModal(false);
    };

    return (
        <StyledPlannerContainer>
            {deviceSize !== 'mobile' && (
                <>
                    <PlannerWrapper>
                        <PlannerCol size={30}>
                            <Planner
                                onDayClick={onDayClick}
                                computeDayProps={dayModifier}
                                monthsRef={viewportRef}
                                isMonthLoaded={isLoaded}
                                onInViewIntervalChange={loadMore}
                                PlannerMonthComponent={ActivityPlannerMonth}
                                initialCenterMonth={selectedDay ?? undefined}
                                isActivity
                            >
                                <ActivityPlannerController onToday={onSetToday} />
                            </Planner>
                        </PlannerCol>
                        <PlannerCol size={70} pSpace={4} dl="flex" ds="none">
                            {selectedDay && timeslotsForSelectedDay && (
                                <ActivityPlannerTimeslots
                                    activity={activity}
                                    className="gt-s"
                                    selectedDay={selectedDay}
                                    timeslots={timeslotsForSelectedDay}
                                    setTimeslot={validatedSetTimeslot}
                                />
                            )}
                        </PlannerCol>
                    </PlannerWrapper>
                    <StyledStickyBar className="lt-s" open={!!selectedDay}>
                        <StyledTextButton onClick={openModal}>
                            <FormattedMessage defaultMessage="Bekijk tickets" />
                        </StyledTextButton>
                    </StyledStickyBar>
                </>
            )}
            {deviceSize === 'mobile' && (
                <Modal
                    open={openPlannerModal ?? false}
                    onClose={onModalClose!}
                    onBack={!openActivityPlanner ? undefined : onBack}
                >
                    <InnerModal>
                        <Box style={{ display: !openActivityPlanner ? 'block' : 'none' }}>
                            <Planner
                                onDayClick={onDayClick}
                                computeDayProps={dayModifier}
                                monthsRef={viewportRef}
                                isMonthLoaded={isLoaded}
                                onInViewIntervalChange={loadMore}
                                PlannerMonthComponent={ActivityPlannerMonth}
                                initialCenterMonth={selectedDay ?? undefined}
                                isActivity
                            >
                                <ActivityPlannerController onToday={onSetToday} />
                            </Planner>
                            <StyledStickyBar className="lt-s" open={!!selectedDay}>
                                <StyledTextButton onClick={onActivityPlanner}>
                                    <FormattedMessage defaultMessage="Bekijk tickets" />
                                </StyledTextButton>
                            </StyledStickyBar>
                        </Box>
                        {openActivityPlanner && !openActivityTickets && (
                            <>
                                {selectedDay && (
                                    <StyledModalHeader>
                                        <Title>
                                            <FormattedMessage
                                                defaultMessage="{optionsQuantity} beschikbare opties"
                                                values={{ optionsQuantity: timeslotsForSelectedDay?.length }}
                                            />
                                        </Title>
                                        <DateTitle>
                                            <FormattedDate value={selectedDay} format="WEEKDAYMONTHYEAR" />
                                        </DateTitle>
                                    </StyledModalHeader>
                                )}
                                {selectedDay && timeslotsForSelectedDay && (
                                    <ActivityPlannerTimeslots
                                        activity={activity}
                                        selectedDay={selectedDay}
                                        timeslots={timeslotsForSelectedDay}
                                        setTimeslot={validatedSetTimeslot}
                                    />
                                )}
                            </>
                        )}
                        {openActivityTickets && (
                            <ActivityTickets
                                activity={activity!}
                                setTicket={setTicket}
                                onClose={onClose}
                                open={open}
                                timeslot={activityTimeslot}
                                ticket={ticket}
                                setStickBar={openActivityTickets}
                            />
                        )}
                    </InnerModal>
                </Modal>
            )}
        </StyledPlannerContainer>
    );
};

const StyledStickyBar = styled(StickyBar)<{ open: boolean }>`
    transform: ${({ open }) => `translateY(${open ? 0 : '100%'})`};
    transition: transform 200ms ease-in-out;
    div {
        display: flex;
        justify-content: center;
    }
`;

const PlannerWrapper = styled.div`
    display: flex;
`;

const PlannerCol = styled.div<{ size: number; pSpace?: number; dl?: string; ds?: string }>`
    width: ${p => `${p.size}%`};
    padding: 0rem ${p => `${p.pSpace}rem`};
    display: ${p => `${p.dl}`};

    @media screen and (max-width: 720px) {
        width: 100%;
        display: ${p => p.ds};
        padding-top: 8rem;
    }
`;

const StyledPlannerContainer = styled(PlannerContainer)`
    @media screen and (max-width: 720px) {
        padding: 0 2rem;
    }
`;

const StyledTextButton = styled.button`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.2rem 3.2rem;
    background-color: ${({ theme }) => theme.colors.primary[80]};
    border-radius: 9.9rem;
    color: ${({ theme }) => theme.colors.neutral[0]};
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 2.4rem;
`;

const StyledModalHeader = styled(ModalHeader)`
    justify-content: flex-start;
    border-bottom: none;
    margin-bottom: 3rem;
`;

const DateTitle = styled(Title)`
    font-size: 1.4rem;
    font-weight: 400;
`;

const InnerModal = styled.div`
    padding-top: 5rem;
`;

export default ActivityPlanner;
